"use client";
/*
 * Documentation:
 * Avatar — https://app.subframe.com/library?component=Avatar_bec25ae6-5010-4485-b46b-cf79e3943ab2
 * Default Page Layout — https://app.subframe.com/library?component=Default+Page+Layout_a57b1c43-310a-493f-b807-8cc88e2452cf
 * Dropdown Menu — https://app.subframe.com/library?component=Dropdown+Menu_99951515-459b-4286-919e-a89e7549b43b
 * Sidebar Collapsible — https://app.subframe.com/library?component=Sidebar+Collapsible_e732d4fd-dae8-4053-bb88-7c02acab53f9
 */

import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import {
  FeatherArrowRight,
  FeatherChevronsUpDown,
  FeatherLayoutDashboard,
  FeatherLogOut,
} from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import { Avatar } from "../components/Avatar";
import { DropdownMenu } from "../components/DropdownMenu";
import { SidebarCollapsible } from "../components/SidebarCollapsible";
import * as SubframeUtils from "../utils";
import { getStoredUserData } from "../../services/api";
import { useAuth } from "../../features/auth";
import { LOGO_PATH, LOGO_ALT } from "../../constants/logo";

/** First character of the first word of display name, for avatar label */
function getFirstNameAvatarLetter(
  userName: string | undefined,
  email: string | undefined,
): string {
  if (userName?.trim()) {
    const firstWord = userName.trim().split(/\s+/)[0];
    const ch = firstWord?.[0];
    if (ch) return ch.toUpperCase();
  }
  if (email?.trim()) {
    const ch = email.trim()[0];
    if (ch) return ch.toUpperCase();
  }
  return "U";
}

interface DefaultPageLayoutRootProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  className?: string;
}

const DefaultPageLayoutRoot = React.forwardRef<
  HTMLDivElement,
  DefaultPageLayoutRootProps
>(function DefaultPageLayoutRoot(
  { children, className, ...otherProps }: DefaultPageLayoutRootProps,
  ref,
) {
  const { user, isAdminOrCfo } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [footerMenuOpen, setFooterMenuOpen] = useState(false);

  const showAdminDashboardCta =
    isAdminOrCfo() && !location.pathname.startsWith("/admin");

  const handleSignOut = () => {
    // Get user role and master admin info before clearing data
    const currentUser = getStoredUserData();
    const userRole = currentUser?.role?.toLowerCase();
    const masterAdminUrl = localStorage.getItem("master_admin_url");
    const cameFromMasterAdmin = localStorage.getItem("came_from_master_admin");

    localStorage.clear();

    // Determine redirect based on user origin and role
    if (
      cameFromMasterAdmin === "true" &&
      masterAdminUrl &&
      userRole !== "client_view"
    ) {
      // User came from master admin and is not client_view - redirect to master admin
      window.location.href = masterAdminUrl;
    } else {
      // client_view role or normal tenant user - redirect to tenant login page
      window.location.href = "/";
    }
  };

  return (
    <div
      className={SubframeUtils.twClassNames(
        "flex h-screen w-full items-start",
        className,
      )}
      ref={ref}
      {...otherProps}
    >
      <SidebarCollapsible
        expanded={footerMenuOpen}
        header={
          <img
            alt={LOGO_ALT}
            className="h-6 flex-none object-cover"
            src={LOGO_PATH}
          />
        }
        footer={
          <SubframeCore.DropdownMenu.Root onOpenChange={setFooterMenuOpen}>
            <SubframeCore.DropdownMenu.Trigger asChild={true}>
              <div className="flex w-full min-w-0 max-w-full cursor-pointer items-center gap-2">
                <Avatar className="h-8 w-8 flex-none shrink-0">
                  {getFirstNameAvatarLetter(user?.userName, user?.email)}
                </Avatar>
                <div
                  className={SubframeUtils.twClassNames(
                    "min-w-0 flex-1 flex-col items-stretch overflow-hidden",
                    footerMenuOpen
                      ? "flex"
                      : "hidden group-hover/e732d4fd:flex",
                  )}
                >
                  <span
                    className="w-full truncate text-left text-caption-bold font-caption-bold text-default-font"
                    title={user?.userName ?? undefined}
                  >
                    {user?.userName ?? ""}
                  </span>
                  <span
                    className="w-full truncate text-left text-caption font-caption text-subtext-color"
                    title={user?.role ?? undefined}
                  >
                    {user?.role ?? ""}
                  </span>
                </div>
                <FeatherChevronsUpDown
                  className={SubframeUtils.twClassNames(
                    "h-4 w-4 flex-none shrink-0 text-body font-body text-default-font",
                    footerMenuOpen
                      ? "block"
                      : "hidden group-hover/e732d4fd:block",
                  )}
                />
              </div>
            </SubframeCore.DropdownMenu.Trigger>
            <SubframeCore.DropdownMenu.Portal>
              <SubframeCore.DropdownMenu.Content
                side="bottom"
                align="start"
                sideOffset={8}
                asChild={true}
              >
                <DropdownMenu>
                  {/* <DropdownMenu.DropdownItem icon={<FeatherUserPlus />}>
                    Invite team
                  </DropdownMenu.DropdownItem>
                  <DropdownMenu.DropdownItem icon={<FeatherSettings />}>
                    Settings
                  </DropdownMenu.DropdownItem> */}
                  <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-200" />
                  <DropdownMenu.DropdownItem
                    icon={<FeatherLogOut />}
                    onClick={handleSignOut}
                  >
                    Sign out
                  </DropdownMenu.DropdownItem>
                </DropdownMenu>
              </SubframeCore.DropdownMenu.Content>
            </SubframeCore.DropdownMenu.Portal>
          </SubframeCore.DropdownMenu.Root>
        }
        footerEnd={
          showAdminDashboardCta ? (
            <div
              className={SubframeUtils.twClassNames(
                "w-full px-1",
                footerMenuOpen ? "block" : "hidden group-hover/e732d4fd:block",
              )}
            >
              <button
                type="button"
                onClick={() => navigate("/admin")}
                className={SubframeUtils.twClassNames(
                  "group/admin-cta flex w-full cursor-pointer items-center gap-3 rounded-lg border border-solid border-neutral-200 bg-gradient-to-br from-neutral-50 to-neutral-100/80 px-3 py-2.5 text-left shadow-sm transition-all",
                  "hover:border-brand-300 hover:from-brand-50 hover:to-brand-50/90 hover:shadow-md",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
                )}
              >
                <div className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-brand-100 text-brand-700 transition-colors group-hover/admin-cta:bg-brand-200">
                  <FeatherLayoutDashboard className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1 overflow-hidden">
                  <span className="block text-caption-bold font-caption-bold text-default-font">
                    Go to admin dashboard
                  </span>
                  <span className="block truncate text-caption font-caption text-subtext-color">
                    {"Users, roles & company settings"}
                  </span>
                </div>
                <FeatherArrowRight className="h-4 w-4 flex-none text-subtext-color transition-transform group-hover/admin-cta:translate-x-0.5 group-hover/admin-cta:text-brand-700" />
              </button>
            </div>
          ) : null
        }
      >
        {/* <SidebarCollapsible.NavItem icon={<FeatherHome />} selected={true}>
          Home
        </SidebarCollapsible.NavItem>
        <SidebarCollapsible.NavItem icon={<FeatherInbox />}>
          Inbox
        </SidebarCollapsible.NavItem>
        <SidebarCollapsible.NavItem icon={<FeatherBarChart2 />}>
          Reports
        </SidebarCollapsible.NavItem> */}
      </SidebarCollapsible>
      {children ? (
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4 self-stretch overflow-y-auto scroll-pb-40 bg-default-background">
          {children}
        </div>
      ) : null}
    </div>
  );
});

export const DefaultPageLayout = DefaultPageLayoutRoot;
